import AnimationFactory from './AnimationFactory.js';
import AnimationGroup from './AnimationGroup.js';
import IntersectionWatcher from './watchers/IntersectionWatcher.js';
import YAxisWatcher from './watchers/YAxisWatcher.js';
import MojsAnimation from './animations/MojsAnimation.js';
import NumberAnimation from './animations/NumberAnimation.js';

class Animate {
	constructor(config) {
		this.config = Object.assign({
			scope: '[data-animate]',
			delay: 0,
			duration: 400,
			groupedDelay: 100,
			defaultType: 'mojs',
			respectReducedMotion: true,
			observer: {
				rootMargin: '0px',
				threshold: 0
			},
			animationTypes: {
				mojs: MojsAnimation,
				number: NumberAnimation
			}
		}, config);

		this.$scope = $();
		this.states = new WeakMap();
		this.groups = {};
		this.pendingAnimations = [];

		this.factory = new AnimationFactory(this.config);

		let onChanges = this.handleVisibilityChanges.bind(this);
		this.intersectionWatcher = new IntersectionWatcher(this.config.observer, onChanges);
		this.yAxisWatcher = new YAxisWatcher(onChanges);

		this.refresh();
	}

	prefersReducedMotion() {
		return this.config.respectReducedMotion
			&& typeof window.matchMedia === 'function'
			&& window.matchMedia('(prefers-reduced-motion: reduce)').matches;
	}

	refresh() {
		if (this.prefersReducedMotion()) return;

		this.$scope = $(this.config.scope);

		let $new = this.$scope.filter((index, scope) => !this.states.has(scope));

		if ($new.length === 0) return;

		$new.each((index, scope) => this.register($(scope)));

		this.yAxisWatcher.check();
	}

	destroy() {
		this.intersectionWatcher.destroy();
		this.yAxisWatcher.destroy();

		this.pendingAnimations.forEach(({ record, timeoutId }) => {
			clearTimeout(timeoutId);
			record.isAnimated = false;
		});
		this.pendingAnimations = [];

		this.$scope.each((index, scope) => {
			let record = this.states.get(scope);
			if (!record) return;

			if (!record.isAnimated) {
				record.instance.destroy();
				this.states.delete(scope);
			}
		});

		this.$scope = $();
		this.groups = {};
	}

	register($scope) {
		let instance = this.factory.create($scope);
		if (!instance) return;

		let record = {
			element: $scope[0],
			instance: instance,
			isAnimated: false,
			inViewport: false,
			groupName: $scope.data('animate-group') || null,
			prio: $scope.data('prio'),
			groupedDelay: $scope.data('grouped-delay'),
			yOnly: $scope.data('animate-y-only') !== undefined
		};

		this.states.set(record.element, record);

		if (record.groupName) {
			if (!this.groups[record.groupName]) {
				this.groups[record.groupName] = new AnimationGroup(this.config.groupedDelay);
			}
			this.groups[record.groupName].add(record);
		}

		this.watcherFor(record).watch(record.element);
	}

	watcherFor(record) {
		return record.yOnly ? this.yAxisWatcher : this.intersectionWatcher;
	}

	handleVisibilityChanges(changes) {
		let affectedGroups = new Set();

		changes.forEach(({ element, inViewport }) => {
			let record = this.states.get(element);
			if (!record) return;

			record.inViewport = inViewport;

			if (!inViewport) return;
			if (record.isAnimated) return;

			if (record.groupName) {
				affectedGroups.add(record.groupName);
			} else {
				this.startAnimation(record);
			}
		});

		affectedGroups.forEach(groupName => this.animateGroup(groupName));
	}

	animateGroup(groupName) {
		let group = this.groups[groupName];
		if (!group) return;

		group.claimReady(Date.now()).forEach(({ record, delay }) => {
			this.startAnimation(record, delay);
		});
	}

	startAnimation(record, delay = 0) {
		record.isAnimated = true;
		this.watcherFor(record).unwatch(record.element);

		if (delay > 0) {
			this.scheduleAnimation(record, delay);
		} else {
			record.instance.animate();
		}
	}

	scheduleAnimation(record, delay) {
		let pending = { record };

		pending.timeoutId = setTimeout(() => {
			this.pendingAnimations = this.pendingAnimations.filter(p => p !== pending);
			record.instance.animate();
		}, delay);

		this.pendingAnimations.push(pending);
	}

}

export default Animate;
